import { createFileRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router'
import { captureException, track } from '@turfchale/analytics'
import { supabase, useAuthStore } from '@turfchale/supabase'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { Logo } from '@/components/ui/Logo'

export const Route = createFileRoute('/auth/')({
  beforeLoad: ({ search }) => {
    const { profile } = useAuthStore.getState()
    if (!profile) return
    throw redirect({ to: (search as { redirect?: string }).redirect ?? '/app' })
  },
  component: AuthPage,
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
})

function AuthPage() {
  const navigate = useNavigate()
  const { redirect: redirectParam } = useSearch({ from: '/auth/' })

  const [view, setView] = useState<'idle' | 'sent'>('idle')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)

  useEffect(() => {
    if (countdown <= 0) return
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000)
    return () => clearTimeout(t)
  }, [countdown])

  const handleGoogleSignIn = async () => {
    const searchParams = redirectParam ? `?redirect=${encodeURIComponent(redirectParam)}` : ''
    const { error } = await supabase.auth.signInWithOAuth({
      options: { redirectTo: `${window.location.origin}/auth/callback${searchParams}` },
      provider: 'google',
    })
    if (error) {
      track('auth_sign_in_failed', { error: error.message })
      captureException(error, { context: 'google_sign_in' })
      toast.error(error.message)
    }
  }

  const callbackUrl = `${window.location.origin}/auth/callback${
    redirectParam ? `?redirect=${encodeURIComponent(redirectParam)}` : ''
  }`

  const handleSendOtp = async () => {
    if (!email) return
    setLoading(true)
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: callbackUrl },
    })
    setLoading(false)
    if (error) {
      track('auth_sign_in_failed', { error: error.message })
      captureException(error, { context: 'otp_send' })
      toast.error(error.message)
      return
    }
    setView('sent')
    setCountdown(30)
  }

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) return
    setLoading(true)
    const { error } = await supabase.auth.verifyOtp({ email, token: otp, type: 'email' })
    setLoading(false)
    if (error) {
      track('auth_sign_in_failed', { error: error.message })
      captureException(error, { context: 'otp_verify' })
      toast.error(error.message)
      return
    }
    navigate({ search: { redirect: redirectParam }, to: '/auth/callback' })
  }

  const handleResend = async () => {
    setLoading(true)
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: callbackUrl },
    })
    setLoading(false)
    if (error) {
      toast.error(error.message)
      return
    }
    setCountdown(30)
  }

  const maskedEmail = email.replace(/(.{2})(.*)(@.*)/, (_, a, _b, c) => `${a}...${c}`)

  return (
    <div
      style={{
        alignItems: 'center',
        background: 'var(--bg)',
        display: 'flex',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '24px',
      }}
    >
      <div
        style={{
          background: 'var(--bg2)',
          border: '1px solid var(--lime-line)',
          borderRadius: '16px',
          maxWidth: '400px',
          padding: '40px',
          textAlign: 'center',
          width: '100%',
        }}
      >
        <div style={{ marginBottom: '8px' }}>
          <Logo fontSize={26} />
        </div>

        {view === 'idle' ? (
          <>
            <p style={{ color: 'var(--muted)', fontSize: '14px', marginBottom: '32px' }}>
              Sign in to book your turf
            </p>

            <button
              disabled={loading}
              onClick={handleGoogleSignIn}
              style={{
                alignItems: 'center',
                background: 'var(--text)',
                border: 'none',
                borderRadius: '10px',
                color: '#0b1c10',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                fontSize: '15px',
                fontWeight: 600,
                gap: '10px',
                justifyContent: 'center',
                opacity: loading ? 0.6 : 1,
                padding: '12px 20px',
                width: '100%',
              }}
            >
              <GoogleIcon />
              Continue with Google
            </button>

            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                gap: '12px',
                margin: '20px 0',
              }}
            >
              <div style={{ background: 'var(--lime-line)', flex: 1, height: '1px' }} />
              <span style={{ color: 'var(--muted)', fontSize: '13px' }}>or</span>
              <div style={{ background: 'var(--lime-line)', flex: 1, height: '1px' }} />
            </div>

            <input
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
              placeholder="Enter your email"
              style={{
                background: 'var(--bg3)',
                border: '1px solid var(--lime-line)',
                borderRadius: '10px',
                color: 'var(--text)',
                fontSize: '15px',
                marginBottom: '12px',
                outline: 'none',
                padding: '12px 16px',
                width: '100%',
              }}
              type="email"
              value={email}
            />
            <button
              disabled={loading || !email}
              onClick={handleSendOtp}
              style={{
                background: 'var(--lime)',
                border: 'none',
                borderRadius: '10px',
                color: '#0b1c10',
                cursor: loading || !email ? 'not-allowed' : 'pointer',
                fontSize: '15px',
                fontWeight: 600,
                opacity: loading || !email ? 0.6 : 1,
                padding: '12px 20px',
                width: '100%',
              }}
            >
              {loading ? 'Sending…' : 'Send code →'}
            </button>
          </>
        ) : (
          <>
            <p style={{ color: 'var(--muted)', fontSize: '14px', marginBottom: '8px' }}>
              Check your email
            </p>
            <p style={{ color: 'var(--text-dim)', fontSize: '13px', marginBottom: '24px' }}>
              We sent a 6-digit code to {maskedEmail}
            </p>

            <input
              autoFocus
              inputMode="numeric"
              maxLength={6}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handleVerifyOtp()}
              placeholder="Enter 6-digit code"
              style={{
                background: 'var(--bg3)',
                border: '1px solid var(--lime-line)',
                borderRadius: '10px',
                color: 'var(--text)',
                fontSize: '22px',
                fontWeight: 600,
                letterSpacing: '4px',
                marginBottom: '12px',
                outline: 'none',
                padding: '12px 16px',
                textAlign: 'center',
                width: '100%',
              }}
              type="text"
              value={otp}
            />
            <button
              disabled={loading || otp.length !== 6}
              onClick={handleVerifyOtp}
              style={{
                background: 'var(--lime)',
                border: 'none',
                borderRadius: '10px',
                color: '#0b1c10',
                cursor: loading || otp.length !== 6 ? 'not-allowed' : 'pointer',
                fontSize: '15px',
                fontWeight: 600,
                opacity: loading || otp.length !== 6 ? 0.6 : 1,
                padding: '12px 20px',
                width: '100%',
              }}
            >
              {loading ? 'Verifying…' : 'Verify'}
            </button>

            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                gap: '16px',
                justifyContent: 'center',
                marginTop: '16px',
              }}
            >
              <button
                onClick={() => {
                  if (loading) return
                  setView('idle')
                  setOtp('')
                  setLoading(false)
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--muted)',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                ← Back
              </button>
              <span style={{ color: 'var(--lime-line)' }}>·</span>
              <button
                disabled={countdown > 0 || loading}
                onClick={handleResend}
                style={{
                  background: 'none',
                  border: 'none',
                  color: countdown > 0 ? 'var(--muted)' : 'var(--lime)',
                  cursor: countdown > 0 || loading ? 'default' : 'pointer',
                  fontSize: '13px',
                }}
              >
                {countdown > 0 ? `Resend in ${countdown}s` : 'Resend'}
              </button>
            </div>
          </>
        )}

        <button
          onClick={() => navigate({ to: '/' })}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--muted)',
            cursor: 'pointer',
            fontSize: '13px',
            marginTop: '16px',
          }}
        >
          ← Back to home
        </button>
      </div>
    </div>
  )
}

function GoogleIcon() {
  return (
    <svg height="18" viewBox="0 0 18 18" width="18">
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  )
}
